import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  IconButton,
  TextField,
} from '@mui/material';
import MuiTypographyMandatory from '../../../Components/Custom/MuiTypographyMandatory';
import StyledInputField from '../../../Components/Custom/StyledInputField';
import MUITypography from '../../../Components/MUITypography';
import { VM_Colors, align_column } from '../../../Styles/VMStyles';
import axios from 'axios';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import { SESSION_USER_CREATED_BY_KEY, SESSION_USER_ID_KEY } from '../../../Util/VMConstants';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  API_ADD_UPDATE_CHECKLIST,
  API_GET_ADMIN_MEMEBRS,
  API_GET_UPCOMING_ENQUIRIES,
  API_GET_CHECKLIST_POINTS,
  API_GET_CHECKLIST_TYPES,
  API_DELETE_CHECKLIST_TYPE,
} from '../../../Util/URLConstants';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StorageFactory from '../../../Util/StorageFactory';
import dayjs from 'dayjs';
import { returnDate } from '../../../Util/UtilHelper';

function CheckListDrawer({
  isOpen,
  onClose,
  onCreateTask,
  mode = 'create',
  initialData = null,
}) {
  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const ownerUserId = sessionFactory.get(SESSION_USER_CREATED_BY_KEY) || userID;

  const priorityOptions = ['Low', 'Medium', 'High'];

  const [formData, setFormData] = useState({
    name: '',
    department: '',
    customDepartment: '',
    priority: 'Medium',
    event: '',
    assignedTo: null,
    checklistPoint: '',
    checklistPoints: [],
    dueDate: null,
  });

  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [categoryTypes, setCategoryTypes] = useState([]);
  const serverCategoryTypesRef = useRef([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs().endOf('month'));

  /* ---------------- FETCH MEMBERS & CATEGORIES ---------------- */



  useEffect(() => {
    if (!isOpen) return;

    setErrors({});

    const fetchBaseData = async () => {
      try {
        const [membersRes, categoryRes] = await Promise.all([
          axios.get(`${API_GET_ADMIN_MEMEBRS}?user_id=${userID}`),
          axios.get(`${API_GET_CHECKLIST_TYPES}?user_id=${userID}`),
        ]);

        setMembers(membersRes.data?.data || []);
        const types = categoryRes.data?.data || [];
        setCategoryTypes(types);
        serverCategoryTypesRef.current = types;
      } catch (e) {
        console.error(e);
      }
    };

    fetchBaseData();
  }, [isOpen, userID]);

  /* ---------------- PREFILL FOR REUSE ---------------- */

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      const type = initialData.role || initialData.type || '';
      setFormData({
        name: `${initialData.checklist_name} `,
        department: type,
        customDepartment: '',
        priority: initialData.priority || 'Medium',
        event: '',
        assignedTo: initialData.assign_users || null,
        checklistPoint: '',
        checklistPoints: initialData.checklist_points || [],
        dueDate: null,
      });
      return;
    }

    setFormData({
      name: '',
      department: '',
      customDepartment: '',
      priority: 'Medium',
      event: '',
      assignedTo: null,
      checklistPoint: '',
      checklistPoints: [],
      dueDate: null,
    });
  }, [mode, initialData, isOpen]);

  /* ---------------- FETCH EVENTS ---------------- */

  useEffect(() => {
    if (!startDate || !endDate) return;

    axios
      .get(
        `${API_GET_UPCOMING_ENQUIRIES}?user_id=${userID}&start_date=${dayjs(
          startDate
        ).format('YYYY-MM-DD')}&end_date=${dayjs(endDate).format(
          'YYYY-MM-DD'
        )}`
      )
      .then((res) => setEvents(res.data?.data || []))
      .catch(console.error);
  }, [startDate, endDate, userID]);

  /* ---------------- HELPERS ---------------- */

  const getCategoryType = (data = formData) => {
    const type =
      data.department === 'Others'
        ? data.customDepartment.trim()
        : (data.department || '').trim();
    return type && type !== 'Others' ? type : '';
  };

  const extractLatestChecklistPoints = (payload) => {
    const data = payload?.data;
    if (isEmpty(data)) return [];

    if (Array.isArray(data)) {
      if (typeof data[0] === 'string') return data.filter(Boolean);

      const latest = [...data].sort((a, b) => {
        const dateA = new Date(a.updated_date || a.created_date || 0).getTime();
        const dateB = new Date(b.updated_date || b.created_date || 0).getTime();
        return dateB - dateA;
      })[0];

      const points =
        latest?.checklist_points ||
        latest?.points ||
        (Array.isArray(latest) ? latest : []);
      return Array.isArray(points) ? points : [];
    }

    if (typeof data === 'object') {
      const points = data.checklist_points || data.points || [];
      return Array.isArray(points) ? points : [];
    }

    return [];
  };

  const fetchChecklistPointsByType = async (type) => {
    if (!type || type === 'Others') return;
    try {
      const loadPoints = async (uid) => {
        const res = await axios.get(API_GET_CHECKLIST_POINTS, {
          params: { user_id: uid, type },
        });
        return extractLatestChecklistPoints(res.data);
      };

      let points = await loadPoints(userID);
      if (isEmpty(points) && ownerUserId && ownerUserId !== userID) {
        points = await loadPoints(ownerUserId);
      }

      setFormData((p) => ({
        ...p,
        checklistPoints: points,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    if (errors[field]) {
      setErrors((p) => ({ ...p, [field]: '' }));
    }
  };

  const onAddPoint = () => {
    const point = formData.checklistPoint.trim();
    if (!point) return;
    setFormData((p) => ({
      ...p,
      checklistPoints: [...p.checklistPoints, point],
      checklistPoint: '',
    }));
    if (errors.checklistPoints) {
      setErrors((p) => ({ ...p, checklistPoints: '' }));
    }
  };

  const onUpdatePoint = (index, value) => {
    setFormData((prev) => {
      const updated = [...prev.checklistPoints];
      updated[index] = value;
      return { ...prev, checklistPoints: updated };
    });
  };

  const onRemovePoint = (index) => {
    setFormData((prev) => ({
      ...prev,
      checklistPoints: prev.checklistPoints.filter((_, i) => i !== index),
    }));
  };

  const removeCategoryFromState = (type) => {
    setCategoryTypes((prev) => prev.filter((cat) => cat !== type));
    setFormData((prev) => {
      if (prev.department !== type) return prev;
      return {
        ...prev,
        department: '',
        customDepartment: '',
        checklistPoints: [],
      };
    });
  };

  const handleDeleteCategoryType = async (type) => {
    if (!type) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete the "${type}" category?`
    );
    if (!confirmed) return;

    const isServerType = serverCategoryTypesRef.current.some((cat) => cat === type);
    if (!isServerType) {
      removeCategoryFromState(type);
      return;
    }

    try {
      const response = await axios.get(API_DELETE_CHECKLIST_TYPE, {
        params: {
          user_id: userID,
          type,
        },
      });
      const data = response.data;

      if (data.status_code === 200) {
        toast.success(data.message || 'Category type removed successfully');
        serverCategoryTypesRef.current = serverCategoryTypesRef.current.filter(
          (cat) => cat !== type
        );
        removeCategoryFromState(type);
      } else {
        toast.error(data.message || 'Failed to delete category type');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error deleting category type');
    }
  };

  /* ---------------- VALIDATION (UNCHANGED) ---------------- */

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Checklist name is required';
    if (!formData.department) newErrors.department = 'Department is required';
    if (formData.department === 'Others' && !formData.customDepartment.trim()) {
      newErrors.customDepartment = 'Please enter custom department';
    }
    if (!formData.event) newErrors.event = 'Event is required';
    if (!formData.assignedTo) newErrors.assignedTo = 'Please select an assignee';
    if (formData.checklistPoints.length === 0) {
      newErrors.checklistPoints = 'Add at least one checklist point';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Please select a due date';
    } else {
      const due = dayjs(formData.dueDate);
      if (!due.isValid()) newErrors.dueDate = 'Invalid due date';
      else if (due.isBefore(dayjs(), 'day')) {
        newErrors.dueDate = 'Due date cannot be in the past';
      }
    }

    if (!startDate) newErrors.startDate = 'Please select a start date';
    if (!endDate) newErrors.endDate = 'Please select an end date';
    if (startDate && endDate && dayjs(endDate).isBefore(dayjs(startDate), 'day')) {
      newErrors.endDate = 'End date cannot be before start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    const categoryType = getCategoryType();

    const payload = {
      user_id: userID,
      name: formData.name.trim(),
      role: categoryType,
      checklist_points: formData.checklistPoints,
      enquiry_id: formData.event,
      assign_users: formData.assignedTo || [],
      priority: formData.priority,
      due_date: dayjs(formData.dueDate).format('YYYY-MM-DD'),
    };

    try {
      await axios.post(API_ADD_UPDATE_CHECKLIST, payload);
      if (categoryType) {
        await fetchChecklistPointsByType(categoryType);
      }
      toast.success(
        mode === 'reuse'
          ? 'Checklist reused successfully!'
          : 'Checklist created successfully!'
      );
      onCreateTask?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Error saving checklist.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  /* ---------------- UI ---------------- */

  return (
    <Box sx={{ p: 2 }}>
      <StyledInputField
        heading="Checklist Name"
        value={formData.name}
        onChange={(e) => handleChange('name', e.target.value)}
        fullWidth
        required
        error={Boolean(errors.name)}
        helperText={errors.name}
      />

      {/* Category */}
      <Box sx={align_column} mt={2} width="100%">
        <MuiTypographyMandatory label="Category" required />
        <Select
          fullWidth
          value={formData.department}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '__add_category__') {
              handleChange('department', 'Others');
            } else {
              handleChange('department', value);
              if (value && value !== 'Others') {
                fetchChecklistPointsByType(value);
              }
            }
          }}
          displayEmpty
          renderValue={(selected) => selected || '-- Select --'}
          error={Boolean(errors.department)}
          sx={{ backgroundColor: VM_Colors.white }}
        >
           <MenuItem value="" disabled>  -- Select --   </MenuItem>
          {categoryTypes.map((dept) => (
            <MenuItem key={dept} value={dept}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: 1,
                }}
              >
                <span>{dept}</span>
                <IconButton
                  size="small"
                  color="error"
                  aria-label={`Delete ${dept}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteCategoryType(dept);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </MenuItem>
          ))}
          <MenuItem value="__add_category__" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            + Add Category
          </MenuItem>
        </Select>
        {errors.department && (
          <MUITypography fontSize={12} color="error">
            {errors.department}
          </MUITypography>
        )}
      </Box>

      {formData.department === 'Others' && (
        <StyledInputField
          heading="Add Category"
          value={formData.customDepartment}
          onChange={(e) => handleChange('customDepartment', e.target.value)}
          onBlur={() => {
            const trimmed = formData.customDepartment.trim();
            if (trimmed && !categoryTypes.some(cat => cat.toLowerCase() === trimmed.toLowerCase())) {
              setCategoryTypes((prev) => [...prev, trimmed]);
            }
            fetchChecklistPointsByType(trimmed);
          }}
          fullWidth
          error={Boolean(errors.customDepartment)}
          helperText={errors.customDepartment}
        />
      )}

      {/* DATES */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, width: '100%' }}>
          <DesktopDatePicker
            label="Start Date"
            value={startDate}
            onChange={setStartDate}
            slotProps={{
              textField: {
                error: !!errors.startDate,
                helperText: errors.startDate,
                fullWidth: true,
                sx: { backgroundColor: VM_Colors.white },
              },
            }}
          />
          <DesktopDatePicker
            label="End Date"
            value={endDate}
            onChange={setEndDate}
            slotProps={{
              textField: {
                error: !!errors.endDate,
                helperText: errors.endDate,
                fullWidth: true,
                sx: { backgroundColor: VM_Colors.white },
             
              },
            }}
          />
        </Box>
      </LocalizationProvider>

      {/* EVENT */}
      <FormControl fullWidth margin="normal"  sx={{ mt: 2 }} error={!!errors.event} disabled={isSubmitting}>
        <MuiTypographyMandatory label="Select Event" required />
        <Select
          fullWidth
          value={formData.event}
          onChange={(e) => handleChange('event', e.target.value)}
          displayEmpty
          sx={{ backgroundColor: '#FAFAFA', mt: 1 }}
        >
           <MenuItem value="" disabled>  -- Select --   </MenuItem>
          {events.map((event) => (
            <MenuItem key={event.appointment_id} value={event.appointment_id}>
              {event.name+ "  "} 
                 [
              {Array.isArray(event.appointment_date)
                              ? event.appointment_date.map(dateStr => returnDate(dateStr)).join(', ')
                              : returnDate(event.appointment_date)}
              ]
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{errors.event}</FormHelperText>
      </FormControl>

      {/* Checklist Points */}
      <MuiTypographyMandatory label="Enter Checklist Point" sx={{ pt: 1, pb: 0 }}  required={true}/>
      <Box sx={{ display: 'flex', alignItems: 'end', gap: 1, mt: 2, width: '100%' }}>
        <TextField
          value={formData.checklistPoint}
          onChange={(e) => handleChange('checklistPoint', e.target.value)}
          fullWidth
          error={Boolean(errors.checklistPoints)}
          helperText={errors.checklistPoints}
        
        />
        <IconButton
          sx={{
            backgroundColor: VM_Colors.primary,
            color: VM_Colors.white,
            mb: 1,
            '&:hover': { backgroundColor: '#162e66' },
          }}
          onClick={onAddPoint}
        >
          <AddIcon />
        </IconButton>
      </Box>

      {formData.checklistPoints.length > 0 && (
        <Box mt={1}>
          <MuiTypographyMandatory  required label={'Checklist Points:'}/>
          
          <Card variant="outlined" sx={{ mt: 1, border: 'none' }}>
            <CardContent>
              {formData.checklistPoints.map((point, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TextField value={point} onChange={(e) => onUpdatePoint(idx, e.target.value)} fullWidth />
                  <IconButton size="small" color="error" onClick={() => onRemovePoint(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Priority */}
      <Box sx={align_column} mt={2} width="100%">
        <MuiTypographyMandatory label="Priority" required />
        <Select
          fullWidth
          value={formData.priority}
          onChange={(e) => handleChange('priority', e.target.value)}
          sx={{ backgroundColor: VM_Colors.white }}
          displayEmpty
        >
           <MenuItem value="" disabled>  -- Select --   </MenuItem>
          {priorityOptions.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Assigned To */}
      <Box sx={align_column} mt={2} width="100%">
        <MuiTypographyMandatory label="Assigned To" required />
        <Select
          fullWidth
          value={formData.assignedTo?.user_id || ''}
          onChange={(e) => {
            const selected = members.find((m) => m.user_id === e.target.value);
            handleChange('assignedTo', selected || null);
          }}
          displayEmpty
          error={Boolean(errors.assignedTo)}
          sx={{ backgroundColor: VM_Colors.white }}
        >
           <MenuItem value="" disabled>  -- Select --   </MenuItem>
          {members.map((member) => (
            <MenuItem key={member.user_id} value={member.user_id}>
              {member.name}
            </MenuItem>
          ))}
        </Select>
        {errors.assignedTo && (
          <MUITypography fontSize={12} color="error">
            {errors.assignedTo}
          </MUITypography>
        )}
      </Box>

      {/* Due Date */}
      <Box sx={{ mt: 3, width: '100%' }}>
        <MuiTypographyMandatory label="Due Date" required />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            label="Due Date"
            value={formData.dueDate}
            onChange={(newValue) => handleChange('dueDate', newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                sx: { backgroundColor: VM_Colors.white, mt: 1 },
                error: Boolean(errors.dueDate),
                helperText: errors.dueDate || '',
              },
            }}
          />
        </LocalizationProvider>
      </Box>

      <Button
        variant="contained"
        fullWidth
        sx={{ mt: 3 }}
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Saving...' : 'Save Checklist'}
      </Button>
    </Box>
  );
}

export default CheckListDrawer;
